/**
 * Rutinas precargadas de Janeth y de Fernando (Req. 10.8; design.md, 11.3).
 *
 * - Del gimnasio (PLANTILLAS_DE_RUTINAS) y de casa (PLANTILLAS_DE_CASA), cada
 *   una en su modalidad. Las de casa salen de su propio archivo, no de las del
 *   gimnasio. La de casa de Janeth llegó el 26 oct 2026; la de Fernando espera
 *   su revisión.
 * - Descansos confirmados del gimnasio (26 oct 2026): `descansos` de cada
 *   persona. Con ellos también se actualizan, al confirmarlo, las rutinas del
 *   gimnasio que ya se crearon (db/rutinas.ts, aplicarDescansosConfirmados).
 *   Las de casa traen los suyos en cada ejercicio.
 *
 * - Cada teléfono elige de quién es con un toque en Gym (Settings.rutinasDe):
 *   no depende de cómo se escribió el nombre del perfil.
 * - Se revisan antes de crearlas y se crean en una sola transacción. Cada
 *   rutina guarda su clave (Routine.plantilla): no se vuelve a crear aunque se
 *   le cambie el nombre, y nada de lo que ya existe se modifica.
 * - Sin cargas: la primera vez cada ejercicio dice "Primera vez".
 */

import type { Modalidad, PersonaRutinas } from '@/db/types';
import { DESCANSO_DE_JANETH_SEG, GENERAL_DE_JANETH, RUTINA_DE_JANETH } from './rutina-janeth';
import { GENERAL_DE_JANETH_CASA, RUTINA_DE_JANETH_CASA } from './rutina-janeth-casa';
import {
  DESCANSO_DE_FERNANDO_SEG,
  DESCANSO_PESO_MUERTO_SEG,
  GENERAL_DE_FERNANDO,
  PENDIENTES_DE_FERNANDO,
  RUTINA_DE_FERNANDO,
} from './rutina-fernando';

export interface EjercicioDePlantilla {
  exerciseId: string;
  series: number;
  /** null = sin rango, por ejemplo "al fallo" (lo dicen las notas). */
  repsMin: number | null;
  repsMax: number | null;
  /** null = el descanso predeterminado del teléfono, mientras no se confirme otro. */
  descansoSeg: number | null;
  biserie: string | null;
  descansoBiserieSeg: number | null;
  notas: string | null;
  /** Cómo viene en el archivo de origen, si el catálogo lo llama distinto. */
  nombreEnArchivo?: string;
  /** Segundos que se sostiene la posición en cada repetición (rutinas de casa). */
  pausaSeg?: number | null;
  /** Variante del ejercicio, tal como venga en el archivo (rutinas de casa). */
  variante?: string | null;
  /** Se anotan las repeticiones de cada lado; los dos lados son una serie (0.3.5). */
  porLado?: boolean;
}

export interface RutinaDePlantilla {
  /** Clave fija ('janeth.lunes', 'janeth.casa.lunes'): nunca se cambia ni se reutiliza. */
  id: string;
  nombre: string;
  dias: number[];
  notas: string;
  ejercicios: EjercicioDePlantilla[];
  /** Ausente = gimnasio. Las de casa salen de su propio archivo. */
  modalidad?: Modalidad;
}

/**
 * Los descansos confirmados de una persona en el gimnasio (26 oct 2026). Se
 * usan al crear sus rutinas y para actualizar las que ya están en el teléfono.
 */
export interface DescansosConfirmados {
  /** El de todos los ejercicios y el de las biseries, al terminar todos. */
  todosSeg: number;
  /** Los que llevan otro, por id de ejercicio. */
  porEjercicio: Readonly<Record<string, number>>;
  /** Para mostrarlo: "2 min en todos; peso muerto, 3 min". */
  texto: string;
}

/** Lo que trae el archivo pero todavía no se agrega: falta una confirmación. */
export interface PendienteDePlantilla {
  sesion: string;
  /** Tal como viene en el archivo. */
  texto: string;
  pregunta: string;
}

export interface PlantillaDeRutinas {
  persona: PersonaRutinas;
  nombrePersona: string;
  /** La de todas sus rutinas. */
  modalidad: Modalidad;
  /** Sin días fijos: Gym propone la que sigue a la última terminada (A → B → C). */
  ciclo: boolean;
  /** "Lun · Mié · Vie" o "Días variables". */
  pastilla: string;
  tarjeta: { titulo: string; descripcion: string };
  pagina: { antetitulo: string; titulo: string; descripcion: string; boton: string };
  /** El aviso de arriba al revisarlas; sin él, el de "Sin cargas iniciales". */
  aviso?: { titulo: string; texto: string };
  /** Indicaciones para todas sus rutinas. */
  general: string;
  rutinas: readonly RutinaDePlantilla[];
  porConfirmar: readonly PendienteDePlantilla[];
  /** Los confirmados del gimnasio; null en casa, que trae los suyos en cada ejercicio. */
  descansos: DescansosConfirmados | null;
}

/** Las del gimnasio: con sus descansos confirmados. */
export type PlantillaDelGimnasio = PlantillaDeRutinas & { modalidad: 'gimnasio'; descansos: DescansosConfirmados };

export const PLANTILLAS_DE_RUTINAS: Readonly<Record<PersonaRutinas, PlantillaDelGimnasio>> = {
  janeth: {
    persona: 'janeth',
    nombrePersona: 'Janeth',
    modalidad: 'gimnasio',
    ciclo: false,
    pastilla: 'Lun · Mié · Vie',
    tarjeta: {
      titulo: 'Tu rutina acordada.',
      descripcion:
        'Los tres días de tu documento, con biseries, rangos y descansos. Revísala antes de crearla; sin cargas iniciales.',
    },
    pagina: {
      antetitulo: 'Tu rutina acordada',
      titulo: 'Lunes, miércoles y viernes.',
      descripcion: 'De tu documento del 23 de octubre. Revisa que todo esté bien; después la puedes ajustar en cada rutina.',
      boton: 'Crear mis rutinas',
    },
    general: GENERAL_DE_JANETH,
    rutinas: RUTINA_DE_JANETH,
    porConfirmar: [],
    descansos: {
      todosSeg: DESCANSO_DE_JANETH_SEG,
      porEjercicio: {},
      texto: '2 min en todos los ejercicios; en las biseries, al terminar los dos',
    },
  },
  fernando: {
    persona: 'fernando',
    nombrePersona: 'Fernando',
    modalidad: 'gimnasio',
    ciclo: true,
    pastilla: 'Días variables',
    tarjeta: {
      titulo: 'Tus sesiones A, B y C.',
      descripcion:
        'Los bloques lunes, martes y jueves de tu tabla, con series, repeticiones y descansos, para hacerlos el día que quieras. Revísalas antes de crearlas; sin cargas iniciales.',
    },
    pagina: {
      antetitulo: 'Tus sesiones',
      titulo: 'Sesiones A, B y C.',
      descripcion:
        'Los bloques «lunes», «martes» y «jueves» de tu tabla, sin días fijos. Al terminar una sesión, Gym te propone la siguiente; puedes elegir otra cuando quieras.',
      boton: 'Crear mis sesiones',
    },
    general: GENERAL_DE_FERNANDO,
    rutinas: RUTINA_DE_FERNANDO,
    porConfirmar: PENDIENTES_DE_FERNANDO,
    descansos: {
      todosSeg: DESCANSO_DE_FERNANDO_SEG,
      porEjercicio: { ejb_peso_muerto_barra: DESCANSO_PESO_MUERTO_SEG },
      texto: '2 min en todos los ejercicios; peso muerto, 3 min',
    },
  },
};

/**
 * Las de casa, de quien ya mandó su archivo (26 oct 2026: Janeth). La de
 * Fernando espera su revisión: mientras no esté, su teléfono no ofrece nada
 * en Casa.
 */
export const PLANTILLAS_DE_CASA: Readonly<Partial<Record<PersonaRutinas, PlantillaDeRutinas>>> = {
  janeth: {
    persona: 'janeth',
    nombrePersona: 'Janeth',
    modalidad: 'casa',
    ciclo: false,
    pastilla: 'Lun · Mié · Vie',
    tarjeta: {
      titulo: 'Tu rutina de casa.',
      descripcion:
        'Los tres días de tu archivo, solo con tapete: series, repeticiones por lado, pausas e indicaciones. Revísala antes de crearla; tu rutina del gimnasio y su historial no cambian.',
    },
    pagina: {
      antetitulo: 'Tu rutina de casa',
      titulo: 'Solo tapete.',
      descripcion:
        'De tu archivo del 26 de octubre: lunes, miércoles y viernes, de 30 a 40 minutos. Revisa que todo esté bien; después la puedes ajustar en cada rutina.',
      boton: 'Crear mi rutina de casa',
    },
    aviso: {
      titulo: 'Sin carga externa',
      texto:
        'Son de peso corporal: solo repeticiones, sin carga que anotar. En los de un lado escribes las de cada lado y los dos cuentan como una serie. La primera vez cada ejercicio dice "Primera vez".',
    },
    general: GENERAL_DE_JANETH_CASA,
    rutinas: RUTINA_DE_JANETH_CASA,
    porConfirmar: [],
    descansos: null,
  },
};

export const PERSONAS: readonly PersonaRutinas[] = ['janeth', 'fernando'];

/** Las rutinas precargadas de esa persona en esa modalidad; null si no hay. */
export function plantillaDe(
  persona: PersonaRutinas | null | undefined,
  modalidad: Modalidad = 'gimnasio',
): PlantillaDeRutinas | null {
  if (persona !== 'janeth' && persona !== 'fernando') return null;
  return modalidad === 'casa' ? (PLANTILLAS_DE_CASA[persona] ?? null) : PLANTILLAS_DE_RUTINAS[persona];
}

/** Las claves de cada ciclo sin días fijos, en orden (A, B, C). */
export const CICLOS: readonly (readonly string[])[] = PERSONAS.map((p) => PLANTILLAS_DE_RUTINAS[p])
  .filter((p) => p.ciclo)
  .map((p) => p.rutinas.map((r) => r.id));

/** ¿La rutina es una sesión de un ciclo sin días fijos? */
export function esDeCiclo(plantilla: string | null | undefined): boolean {
  return plantilla !== null && plantilla !== undefined && CICLOS.some((c) => c.includes(plantilla));
}

/** El descanso confirmado de un ejercicio en las rutinas del gimnasio de esa persona. */
export function descansoConfirmado(plantilla: PlantillaDelGimnasio, exerciseId: string): number {
  return plantilla.descansos.porEjercicio[exerciseId] ?? plantilla.descansos.todosSeg;
}
